/**
 * The bar of recent check results beside a monitor.
 *
 * ## Why this is the defining element of a monitoring dashboard
 *
 * A row that says "Up · checked 4 minutes ago · 100% over 24h" tells you the present and hides the
 * past. The question an operator actually has is *"has this been steady, or has it been flapping?"*
 * — and that is a shape, not a number. A monitor that failed four times overnight and recovered
 * each time reads as a perfect 100% on a 24-hour average, and reads as obviously unhealthy here.
 *
 * Oldest on the left, newest on the right, because that is the direction a timeline is read.
 *
 * ## Colour is not the only channel
 *
 * Failures are drawn taller as well as red. Roughly one man in twelve cannot reliably separate red
 * from green, and a status display that depends on exactly that distinction fails the people most
 * likely to be woken by it.
 */
export function StatusStrip({
  history,
  className = '',
}: {
  history: boolean[];
  className?: string;
}) {
  if (history.length === 0) {
    return (
      <div className={`text-muted-foreground/70 text-xs ${className}`}>No checks recorded yet</div>
    );
  }

  return (
    <div
      className={`flex items-end gap-[2px] ${className}`}
      role="img"
      aria-label={`Last ${history.length} checks: ${history.filter(Boolean).length} passed, ${
        history.filter((ok) => !ok).length
      } failed`}
    >
      {history.map((healthy, index) => (
        <span
          key={index}
          className={
            healthy
              ? 'bg-success/70 h-3 w-[3px] rounded-full'
              : 'bg-danger h-4 w-[3px] rounded-full'
          }
        />
      ))}
    </div>
  );
}
